import React, { useMemo } from 'react';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { useTranslation } from 'react-i18next';

// One cache per direction. stylis-plugin-rtl mirrors the physical CSS
// properties MUI's `sx` prop generates (left/right, margin-left, etc.) so
// existing components don't need to be rewritten with logical properties —
// switching cache + theme.direction is enough to flip the whole layout.
//
// No `prepend: true` here: that option inserts Emotion's <style> tags at the
// START of <head>, making sx-prop styles lose same-specificity cascade ties
// against plain CSS files (e.g. App.css's `.MuiCard-root` base background) —
// see App.css:24 for the comment relying on the opposite (default/append)
// ordering. Adding it silently broke every card's sx-prop background app-wide.
const cacheLtr = createCache({ key: 'mui' });
const cacheRtl = createCache({ key: 'mui-rtl', stylisPlugins: [prefixer, rtlPlugin] });

export default function RtlThemeProvider({ children }) {
  const { i18n } = useTranslation();
  const isRtl = i18n.language === 'ur';

  const theme = useMemo(() => createTheme({ direction: isRtl ? 'rtl' : 'ltr' }), [isRtl]);

  return (
    <CacheProvider value={isRtl ? cacheRtl : cacheLtr}>
      <ThemeProvider theme={theme}>
        {children}
      </ThemeProvider>
    </CacheProvider>
  );
}
